import { useState } from "react";
import { toast } from "sonner";
import { Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  createBuilding,
  type Building,
  type BuildingType,
} from "@/lib/api/building";
import { parseCoordinateFields } from "@/lib/coordinates";

const BUILDING_TYPES: BuildingType[] = [
  "Toimisto",
  "Liiketila",
  "Logistiikka",
  "Kampus",
  "Asuin",
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful create so Hallitse can refresh and keep manage open. */
  onCreated?: (building: Building) => void;
}

export function AddBuildingDialog({ open, onOpenChange, onCreated }: Props) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [buildingType, setBuildingType] = useState<BuildingType | "">("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [coordError, setCoordError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setName("");
    setAddress("");
    setCity("");
    setBuildingType("");
    setLat("");
    setLon("");
    setCoordError(null);
    setSubmitting(false);
  }

  function handleOpenChange(next: boolean) {
    if (!submitting) {
      if (!next) reset();
      onOpenChange(next);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!buildingType) return;
    const parsedCoords = parseCoordinateFields({ lat, lon });
    if (!parsedCoords.ok) {
      setCoordError(parsedCoords.error);
      return;
    }
    setCoordError(null);
    setSubmitting(true);
    try {
      const building = await createBuilding({
        name: name.trim(),
        address: address.trim(),
        city: city.trim(),
        building_type: buildingType,
        ...(parsedCoords.fields ?? {}),
      });
      toast.success(`${building.name} lisätty`);
      onOpenChange(false);
      reset();
      // Stay in Hallitse: caller refreshes the list so the user can add more buildings
      // or assign this one to a salkku. Konfiguraatio is opened when they select the building.
      onCreated?.(building);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Tallennus epäonnistui");
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Lisää uusi kiinteistö
          </DialogTitle>
          <DialogDescription>
            Perustiedot tallennetaan — voit lisätä historiadatan heti sen
            jälkeen.
          </DialogDescription>
        </DialogHeader>

        <form
          id="add-building-form"
          onSubmit={handleSubmit}
          className="space-y-4 py-1"
        >
          <Field label="Nimi">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="esim. Technopolis Ruoholahti"
              required
              disabled={submitting}
            />
          </Field>

          <Field label="Osoite">
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="esim. Itsehallintokuja 6"
              required
              disabled={submitting}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Kaupunki">
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="esim. Espoo"
                required
                disabled={submitting}
              />
            </Field>

            <Field label="Tyyppi">
              <Select
                value={buildingType}
                onValueChange={(v) => setBuildingType(v as BuildingType)}
                disabled={submitting}
                required
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Valitse…" />
                </SelectTrigger>
                <SelectContent>
                  {BUILDING_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="text-sm">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Koordinaatit (valinnainen)">
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={lat}
                onChange={(e) => {
                  setLat(e.target.value);
                  setCoordError(null);
                }}
                placeholder="Leveysaste, esim. 60.1699"
                inputMode="decimal"
                disabled={submitting}
              />
              <Input
                value={lon}
                onChange={(e) => {
                  setLon(e.target.value);
                  setCoordError(null);
                }}
                placeholder="Pituusaste, esim. 24.9384"
                inputMode="decimal"
                disabled={submitting}
              />
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Jätä tyhjäksi, niin Kairos hakee koordinaatit osoitteesta.
            </p>
            {coordError && (
              <p className="mt-1 text-[11px] text-destructive">{coordError}</p>
            )}
          </Field>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Peruuta
          </Button>
          <Button
            type="submit"
            form="add-building-form"
            disabled={
              submitting ||
              !name.trim() ||
              !address.trim() ||
              !city.trim() ||
              !buildingType
            }
          >
            {submitting ? "Tallennetaan…" : "Lisää kiinteistö"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
